<script>
import { Search } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import Button from "@/shared/ui/button/Button.vue";
import dateFormat from "@/shared/lib/date-format";
import {
  getOrganizationList,
  removeOrganization,
} from "@/entities/organization";
import TableRowActions from "@/shared/ui/custom/components/TableRowActions.vue";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
export default {
  name: "OrganizationList",
  components: {
    TableFooterWrapper,
    Search,
    Input,
    Button,
    TableRowActions,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
  },
  data() {
    return {
      loading: false,
      list: [],
      organizations: null,
      roles: null,
      total: 0,

      filters: {
        q: "",
      },
    };
  },
  computed: {
    canCreate() {
      return this.$store.getters.has_access("ACO");
    },
    actions() {
      const list = [];
      if (this.$store.getters.has_access("AUO")) {
        list.push({
          label: "Редактировать",
          type: "default",
          action: "edit",
          icon: "huge-pen2",
        });
      }
      if (this.$store.getters.has_access("ADO")) {
        list.push({
          label: "Удалить",
          type: "danger",
          action: "remove",
          icon: "huge-danger",
        });
      }
      list.push({
        label: "Просмотр организации",
        type: "default",
        action: "view",
        icon: "huge-eye",
        isLast: true,
      });
      return list;
    },
  },
  mounted() {
    this.getOrganizationList();
  },
  methods: {
    async getOrganizationList() {
      let params = {
        q: this.filters.q.trim() === "" ? null : this.filters.q.trim(),
      };
      this.loading = true;

      try {
        let data = await getOrganizationList(params);
        if (data?.success) {
          if (data?.payload) {
            this.list = data.payload.length ? data.payload : [];
          }
        }
      } finally {
        this.loading = false;
      }
    },
    goToCreate() {
      this.$router.push({
        name: "organization_new",
        params: { id: "new" },
      });
    },
    dateFormat(date) {
      if (!date) {
        return this.$t("Не указано");
      }
      return dateFormat.timestamp_to_local_tz(date, "DD.MM.YYYY, HH:mm");
    },
    search() {
      this.filters.page = 1;
      this.getOrganizationList();
    },
    async handleActions(item) {
      await this.$nextTick();
      if (item.action === "edit") {
        this.$router.push({
          name: "organization_item",
          params: { id: item.id },
        });
      } else if (item.action === "view") {
        this.$router.push({
          name: "organization_item_view",
          params: { id: item.id },
        });
      } else if (item.action === "remove") {
        await this.confirmRemoveOrganization(item.id, item.rowData);
      }
    },
    handleRowClick(row) {
      this.$router.push({
        name: "organization_item_view",
        params: { id: row.id },
      });
    },
    async confirmRemoveOrganization(id, row) {
      if (!id) return;
      const name = row?.name || row?.short_name || `#${id}`;
      try {
        await this.$confirm(
          `${this.$t("Вы уверены, что хотите удалить организацию")} ${name}?`,
          this.$t("Удаление организации"),
          {
            confirmButtonText: this.$t("Удалить"),
            cancelButtonText: this.$t("Отмена"),
            type: "danger",
          }
        );
      } catch {
        return;
      }
      await this.removeOrganization(id);
    },
    async removeOrganization(id) {
      let data = await removeOrganization(id);
      if (data?.success) {
        await this.getOrganizationList();
      }
    },
  },
};
</script>

<template>
  <div class="wrapper-page">
    <div class="flex flex-col h-full w-full gap-8">
      <div class="flex justify-between gap-5">
        <div class="relative" style="width: 320px">
          <Search
            class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          />
          <Input
            v-model="filters.q"
            :placeholder="$t('Поиск по организациям')"
            class="pl-9"
            @input="search"
          />
        </div>
        <Button v-if="canCreate" variant="default" @click="goToCreate"
          >{{ $t("Создать организацию") }} +
        </Button>
      </div>

      <div class="table-area">
        <div class="h-full overflow-auto">
          <Table :loading="loading" :scroll-reset-key="filters.page">
            <TableHeader>
              <TableRow>
                <TableHead class="w-[68px]"></TableHead>
                <TableHead>{{ $t("ID") }}</TableHead>
                <TableHead>{{ $t("Название") }}</TableHead>
                <TableHead>{{ $t("Код") }}</TableHead>
                <TableHead>{{ $t("Телефон") }}</TableHead>
                <TableHead>{{ $t("Email") }}</TableHead>
                <TableHead>{{ $t("Дата создания") }}</TableHead>
                <TableHead>{{ $t("Дата изменения") }}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableEmpty v-if="!loading && !list.length" :colspan="8">
                Нет данных
              </TableEmpty>
              <TableRow
                v-for="row in list"
                :key="row.id"
                class="cursor-pointer"
                @click="handleRowClick(row)"
              >
                <TableCell @click.stop>
                  <TableRowActions
                    :actions="actions"
                    align="end"
                    @action="
                      handleActions({
                        action: $event.action,
                        id: row.id,
                        rowData: row,
                      })
                    "
                  />
                </TableCell>
                <TableCell>{{ row.id || $t("Не указано") }}</TableCell>
                <TableCell>
                  <div class="line-clamp-3">
                    {{ row.name || $t("Не указано") }}
                  </div>
                </TableCell>
                <TableCell>{{ row.code || $t("Не указано") }}</TableCell>
                <TableCell>{{ row.phone || $t("Не указано") }}</TableCell>
                <TableCell>{{ row.email || $t("Не указано") }}</TableCell>
                <TableCell>{{ dateFormat(row.created_at) }}</TableCell>
                <TableCell>{{ dateFormat(row.changed_at) }}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
    <router-view></router-view>
  </div>
</template>

<style scoped lang="scss"></style>
